import { yupResolver } from "@hookform/resolvers/yup";
import { ChevronLeft } from "@mui/icons-material";
import { Button, Stack } from "@mui/material";
import { styled } from "@mui/material/styles";
import { useEffect, useMemo, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import * as Yup from "yup";
import { LoginFormValues } from "../../Models/Auth";
import { RegisterRequired } from "../../Models/PredefinedModels";
import { sendEvent } from "../../Utils/analytics";
import { INK, TEXT_MUTED } from "../../Utils/designTokens";
import { getRemoteValue } from "../../Utils/firebase";
import useAuthState from "../../Utils/store";
import CLoadingButton from "../CLoadingButton/CLoadingButton";
import CToast from "../CToast/CToast";
import FormProvider from "../HookForm/FormProvider";
import RHFBirthDateInput from "../HookForm/RHFBirthDateInput";
import RHFInput from "../HookForm/RHFInput";
import RHFPhoneInput from "../HookForm/RHFPhoneInput";
import ContactIcons from "./ContactIcons";
import { isValidPhone, phoneDigits } from "./phone";


const ContentStyle = styled("div")(({ theme }) => ({
  margin: "auto",
  display: "flex",
  flexDirection: "column",
  justifyContent: "center",
  // CRL-3241 `.auth`
  width: "100%",
  maxWidth: "380px",
  background: "#FFFFFF",
  borderRadius: "16px",
  "@media (min-width: 768.5px)": {
    minHeight: "400px",
  },
}));

const Login = () => {
  const { loginUser, setOtpData } = useAuthState();
  const navigate = useNavigate();

  const [phoneChanged, setPhoneChanged] = useState(false);
  const [register, setRegister] = useState<boolean>(false);
  const [touchedFields, setTouchedFields] = useState<Set<string>>(new Set());

  // Refs for auto-focusing next input
  const emailInputRef = useRef<HTMLInputElement>(null);
  const submitButtonRef = useRef<HTMLButtonElement>(null);

  const ValidationSchema = useMemo(() =>
    Yup.object().shape({
      phoneNumber: Yup.string()
        .required(getRemoteValue("phone_must"))
        .test("phone", getRemoteValue("phone_regex_warning"), (value) =>
          isValidPhone(value ?? "")
        ),
      identityNo: Yup.string()
        .test(
          "conditional-required",
          getRemoteValue("identity_number_must"),
          function (value) {
            // Only require when phoneChanged or register is true
            if ((phoneChanged || register) && !value) {
              return false;
            }
            return true;
          }
        )
        .test(
          "turkish-identity",
          getRemoteValue("identity_regex_warning"),
          function (value) {
            // Only validate Turkish identity when phoneChanged or register is true
            if (!phoneChanged && !register) return true;
            if (!value) return false;
            return validateTurkishIdentityNo(value);
          }
        ),
      name: Yup.string()
        .test(
          "conditional-required",
          getRemoteValue("name_must"),
          function (value) {
            if (register && !value) {
              return false;
            }
            return true;
          }
        )
        .test(
          "min-length",
          getRemoteValue("name_error_min_length"),
          function (value) {
            if (!register || !value) return true;
            return value.trim().length >= 2;
          }
        )
        .test(
          "valid-name",
          getRemoteValue("name_error_invalid"),
          function (value) {
            if (!register || !value) return true;
            // Allow Turkish and English letters, spaces, and common special characters in names
            return /^[a-zA-ZçÇğĞıİöÖşŞüÜ\s'-]+$/.test(value.trim());
          }
        ),
      lastName: Yup.string()
        .test(
          "conditional-required",
          getRemoteValue("lastname_must"),
          function (value) {
            if (register && !value) {
              return false;
            }
            return true;
          }
        )
        .test(
          "min-length",
          getRemoteValue("lastname_error_min_length"),
          function (value) {
            if (!register || !value) return true;
            return value.trim().length >= 2;
          }
        )
        .test(
          "valid-lastname",
          getRemoteValue("lastname_error_invalid"),
          function (value) {
            if (!register || !value) return true;
            // Allow Turkish and English letters, spaces, and common special characters in names
            return /^[a-zA-ZçÇğĞıİöÖşŞüÜ\s'-]+$/.test(value.trim());
          }
        ),
      email: Yup.string()
        .test(
          "conditional-required",
          getRemoteValue("email_must"),
          function (value) {
            if (register && !value) {
              return false;
            }
            return true;
          }
        )
        .test(
          "valid-email",
          getRemoteValue("birthdate_email_invalid"),
          function (value) {
            if (!register || !value) return true;
            // Standard email regex
            return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim());
          }
        ),
      birthDate: Yup.string()
        .test(
          "conditional-required",
          getRemoteValue("birthdate_must"),
          function (value) {
            if (register && !value) {
              return false;
            }
            return true;
          }
        )
        .test(
          "valid-date",
          getRemoteValue("birthdate_error_invalid"),
          function (value) {
            if (!register || !value) return true;
            // Check format DD/MM/YYYY
            const dateRegex = /^\d{2}\/\d{2}\/\d{4}$/;
            if (!dateRegex.test(value)) return false;

            const [day, month, year] = value.split('/').map(Number);
            const date = new Date(year, month - 1, day);

            // Validate that the date is real
            return (
              date.getFullYear() === year &&
              date.getMonth() === month - 1 &&
              date.getDate() === day
            );
          }
        )
        .test(
          "age-validation",
          getRemoteValue("birthdate_error_min_length"),
          function (value) {
            if (!register || !value) return true;

            const [day, month, year] = value.split('/').map(Number);
            const birthDate = new Date(year, month - 1, day);
            const today = new Date();

            // Calculate age
            let age = today.getFullYear() - birthDate.getFullYear();
            const monthDiff = today.getMonth() - birthDate.getMonth();

            if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
              age--;
            }
            debugger

            return age >= 18;
          }
        )
        .test(
          "not-future-date",
          getRemoteValue("birthdate_error_invalid"),
          function (value) {
            if (!register || !value) return true;

            const [day, month, year] = value.split('/').map(Number);
            const birthDate = new Date(year, month - 1, day);
            const today = new Date();

            return birthDate <= today;
          }
        )
        .test(
          "reasonable-age",
          getRemoteValue("birthdate_error_invalid"),
          function (value) {
            if (!register || !value) return true;

            const [day, month, year] = value.split('/').map(Number);
            const birthDate = new Date(year, month - 1, day);
            const today = new Date();

            // Check if person is not older than 120 years
            let age = today.getFullYear() - birthDate.getFullYear();
            return age <= 120;
          }
        ),
    }), [phoneChanged, register]);

  const methodsSubmit = useForm<LoginFormValues>({
    mode: "onChange",
    resolver: yupResolver(ValidationSchema),
    defaultValues: {
      phoneNumber: "",
      identityNo: undefined,
      name: undefined,
      lastName: undefined,
      email: undefined,
      birthDate: undefined,
    },
  });

  const {
    handleSubmit,
    control,
    formState: { isValid, errors, isSubmitting },
    trigger,
    setValue,
  } = methodsSubmit;

  useEffect(() => {
    if (isValid) {
      console.log('isValid', isValid);
      console.log('errors', errors);
    }
    setIsDisabled(!isValid);
  }, [isValid, errors]);

  const [isDisabled, setIsDisabled] = useState(true);

  // Generic function to check if a field should show error
  const shouldShowError = (fieldName: keyof LoginFormValues): boolean => {
    return !!errors[fieldName] && touchedFields.has(fieldName);
  };

  const onSubmit = async (data: LoginFormValues) => {
    const phoneNumberFormatting = phoneDigits(data.phoneNumber);
    const identityNoFormatting = data.identityNo?.replace(/\D/g, "");
    sendEvent(`click_send_otp_button`, {});
    const resData = await loginUser(phoneNumberFormatting, phoneChanged ? identityNoFormatting : undefined);
    if (resData) {
      if (resData.data instanceof RegisterRequired) {
        //TODO: uncomment when register is enabled
        CToast(getRemoteValue("user_error_not_registered"), "error");
        //   onRegisterStateChanged(true);
        return
      }
      setOtpData(resData.data);
      navigate(resData.route, {
        state: {
          phoneNumber: data.phoneNumber,
          identityNo: phoneChanged ? data.identityNo : undefined,
        },
        replace: true,
      });
    }
  };

  const handleWhatsAppClick = () => {
    window.open(`${getRemoteValue("colendi_info_whatsapp")}`, "_blank");
  };

  const handleMailClick = () => {
    window.open(`mailto:${getRemoteValue("colendi_info_mail")}`, "_blank");
  };

  const handleCallCenterClick = () => {
    window.open(`tel:${getRemoteValue("colendi_info_call_center")}`, "_blank");
  };

  const handleFocus = (fieldName: string) => {
    methodsSubmit.clearErrors(fieldName as keyof LoginFormValues);
  };

  const handleBlur = async (fieldName: string) => {
    // Mark field as touched
    setTouchedFields((prev) => new Set(prev).add(fieldName));
    await trigger(fieldName as keyof LoginFormValues);
  };

  const onPhoneChangeClick = () => {
    onRegisterStateChanged(false);
    const newPhoneChangedState = !phoneChanged;
    setPhoneChanged(newPhoneChangedState);
    // If switching from phoneChanged back to normal login, clear identityNo touched state
    if (!newPhoneChangedState) {
      setTouchedFields((prev) => {
        const newSet = new Set(prev);
        newSet.delete('identityNo');
        return newSet;
      });
    }
    // Re-trigger validation after phone changed state toggle
    setTimeout(() => trigger(), 0);
    // navigate("/identity-login");
  };

  // Functions to manually clear form fields
  const clearIdentityNo = () => {
    setValue('identityNo', '', { shouldValidate: false });
  };

  const clearName = () => {
    setValue('name', '', { shouldValidate: false });
  };

  const clearLastName = () => {
    setValue('lastName', '', { shouldValidate: false });
  };

  const clearEmail = () => {
    setValue('email', '', { shouldValidate: false });
  };

  const clearBirthDate = () => {
    setValue('birthDate', '', { shouldValidate: false });
  };

  const handleIdentityNoChange = (value: string) => {
    // Remove any non-numeric characters
    const cleanValue = value.replace(/\D/g, '');

    // When identity number reaches 11 characters, auto-focus next field
    if (cleanValue.length === 11) {
      setTimeout(() => {
        if (register && emailInputRef.current) {
          // In register mode, focus email field
          emailInputRef.current.focus();
        } else if (!register && submitButtonRef.current) {
          // In phoneChanged mode (no email field), focus submit button
          submitButtonRef.current.focus();
        }
      }, 100); // Small delay to ensure state updates complete
    }
  };

  const onRegisterStateChanged = (state: boolean) => {
    if (!state) {
      clearIdentityNo();
      clearName();
      clearLastName();
      clearEmail();
      clearBirthDate();
      setPhoneChanged(false);
      // Clear touched state for register-specific fields
      setTouchedFields((prev) => {
        const newSet = new Set(prev);
        newSet.delete('identityNo');
        newSet.delete('name');
        newSet.delete('lastName');
        newSet.delete('email');
        newSet.delete('birthDate');
        return newSet;
      });
    }
    setRegister(state);
    // Re-trigger validation after state change
    setTimeout(() => trigger(), 0);
  };

  return (
    <ContentStyle>
      {register && (
        <Stack direction="row" justifyContent="start" sx={{ mb: 2 }}>
          <Button
            onClick={() => {
              onRegisterStateChanged(false)
            }}
            startIcon={<ChevronLeft />}
            sx={{
              pt: 2,
              color: '#191B1E',
              textTransform: 'none',
              fontSize: '16px',
              fontWeight: 500,
              '&:hover': {
                backgroundColor: 'rgba(25, 27, 30, 0.04)',
              },
            }}
          >
            {getRemoteValue("go_back")}
          </Button>
        </Stack>
      )}
      {/* CRL-3241 `.auth__title` / `.auth__desc` */}
      <h1
        className="m-0 mb-3 text-[22px] lg:text-[26px] font-extrabold leading-tight"
        style={{ color: INK, letterSpacing: "-0.4px" }}
      >
        {register ? getRemoteValue("register") : phoneChanged ? getRemoteValue("login_with_identity") : getRemoteValue("welcome")}
      </h1>
      <p className="m-0 mb-[26px] text-[15px]" style={{ color: TEXT_MUTED, lineHeight: 1.55 }}>
        {register ? getRemoteValue("register_description") : phoneChanged ? getRemoteValue("login_with_identity_description") : getRemoteValue("login_info")}
      </p>

      <FormProvider methods={methodsSubmit} onSubmit={handleSubmit(onSubmit)}>
        {/* `.field` blocks — every input carries its own reserved error line */}
        <Stack gap="16px" sx={{ mb: "16px" }}>
          <RHFPhoneInput
            name={"phoneNumber"}
            label={getRemoteValue("your_phone_number")}
            control={control}
            errors={touchedFields.has('phoneNumber') ? errors : {}}
            onFocus={() => handleFocus("phoneNumber")}
            onBlur={() => handleBlur("phoneNumber")}
          />

          {register && (
            <Stack direction="row" gap={2}>
              <RHFInput
                name={"name"}
                label={getRemoteValue("your_name")}
                control={control}
                error={shouldShowError('name') ? errors.name : undefined}
                maxLength={50}
                type="text"
                onFocus={() => handleFocus("name")}
                onBlur={() => handleBlur("name")}
              />
              <RHFInput
                name={"lastName"}
                label={getRemoteValue("your_last_name")}
                control={control}
                error={shouldShowError('lastName') ? errors.lastName : undefined}
                maxLength={50}
                type="text"
                onFocus={() => handleFocus("lastName")}
                onBlur={() => handleBlur("lastName")}
              />
            </Stack>
          )}
          {(phoneChanged || register) && (
            <RHFInput
              name={"identityNo"}
              label={getRemoteValue("your_identity_number")}
              control={control}
              error={shouldShowError('identityNo') ? errors.identityNo : undefined}
              maxLength={11}
              type="text"
              onFocus={() => handleFocus("identityNo")}
              onBlur={() => handleBlur("identityNo")}
              onChange={handleIdentityNoChange}
            />
          )}
          {register && (
            <RHFInput
              name={"email"}
              label={getRemoteValue("your_email")}
              control={control}
              error={shouldShowError('email') ? errors.email : undefined}
              maxLength={45}
              type="text"
              inputRef={emailInputRef}
              onFocus={() => handleFocus("email")}
              onBlur={() => handleBlur("email")}
            />
          )}
          {register && (
            <RHFBirthDateInput
              name={"birthDate"}
              label={getRemoteValue("your_birth_date")}
              control={control}
              errors={shouldShowError('birthDate') ? errors : {}}
              maxLength={10}
              type="text"
              onFocus={() => handleFocus("birthDate")}
              onBlur={() => handleBlur("birthDate")}
            />
          )}
        </Stack>

        {/* `.btn.btn--primary` */}
        <CLoadingButton
          ref={submitButtonRef}
          fullWidth
          type="submit"
          variant="contained"
          loading={isSubmitting}
          disabled={isDisabled}
          sx={{ height: "54px", fontSize: "15.5px", fontWeight: 700 }}
        >
          {getRemoteValue("password_send")}
        </CLoadingButton>

        {/* `.auth__contact`: title + "Numaram değişti" link, then icon circles */}
        <div className="mt-7">
          <div className="flex items-center justify-between mb-3">
            {!register ? (
              <span className="text-[15px] font-bold" style={{ color: INK }}>
                {getRemoteValue("communication")}
              </span>
            ) : (
              <span />
            )}
            <button
              type="button"
              className="p-0 bg-transparent border-0 cursor-pointer text-[14px] font-semibold hover:underline"
              style={{ color: INK }}
              onClick={onPhoneChangeClick}
            >
              {phoneChanged ? getRemoteValue("login_with_phone") : getRemoteValue("my_phone_changed")}
            </button>
          </div>
          {!register && (
            <ContactIcons
              onMail={handleMailClick}
              onWhatsApp={handleWhatsAppClick}
              onCallCenter={handleCallCenterClick}
            />
          )}
        </div>
      </FormProvider>
    </ContentStyle>
  );
};

const validateTurkishIdentityNo = (identityNo: string): boolean => {
  // Remove any non-numeric characters
  const cleanedNumber = identityNo.replace(/\D/g, "");

  // Check if it's 11 digits long
  if (cleanedNumber.length !== 11) {
    return false;
  }

  // Check if first digit is not 0
  if (cleanedNumber[0] === "0") {
    return false;
  }

  // Convert to array of numbers
  const digits = cleanedNumber.split("").map(Number);

  // Check if all digits are the same (invalid case)
  if (digits.every(digit => digit === digits[0])) {
    return false;
  }

  // Algorithm 1: Sum of first 10 digits modulo 10 should equal the 11th digit
  const sumFirst10 = digits.slice(0, 10).reduce((sum, digit) => sum + digit, 0);
  if (sumFirst10 % 10 !== digits[10]) {
    return false;
  }

  // Algorithm 2: (sum of odd positioned digits * 7 - sum of even positioned digits) % 10 should equal 10th digit
  const sumOddPositions = digits[0] + digits[2] + digits[4] + digits[6] + digits[8];
  const sumEvenPositions = digits[1] + digits[3] + digits[5] + digits[7];
  const checkDigit = (sumOddPositions * 7 - sumEvenPositions) % 10;

  if (checkDigit !== digits[9]) {
    return false;
  }

  return true;
};

export default Login;
